import React from 'react'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import type { PropertyListings } from '@/types/PropertyListings';
import { Badge } from '@/components/ui/badge';
import Helper from '@/lib/helper';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Edit, Trash, Sparkles, MessageSquare } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { IntakeSessionSummary } from '@/components/listings/ai/aiIntake';
import { progressLabel, relativeTime } from '@/components/listings/ai/useAIConversations';

interface _DraftListingTable {
    loading: boolean;
    listings: PropertyListings[] | null;
    startIndex?: number;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedListingId: React.Dispatch<React.SetStateAction<number | null>>;
    setActionType: React.Dispatch<React.SetStateAction<'delete' | 'deactivate' | 'close' | 'reopen' | 'activate' | 'repost'>>;
    /**
     * AI conversations that have not finished becoming listings. They are
     * drafts too, so they sit here as rows, above the listings, with
     * Continue where a listing has Edit.
     */
    conversations?: IntakeSessionSummary[];
}

const DraftListingTable: React.FC<_DraftListingTable> = ({
    loading,
    listings,
    startIndex = 0,
    setOpen,
    setSelectedListingId,
    setActionType,
    conversations = [],
}) => {
    const triggerAction = (id: number, type: 'delete' | 'deactivate' | 'close' | 'reopen'| 'activate' | 'repost') => {
        setSelectedListingId(id);
        setActionType(type);
        setOpen(true);
    };

    return (
        <TooltipProvider delayDuration={0}>
            <Table className='mt-5'>
                <TableHeader>
                    <TableRow>
                        <TableHead className='w-[500px]'>Listing</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {loading ? (
                        <TableRow>
                            <TableCell colSpan={4}>
                                <div className='flex justify-center py-4'>
                                    <Spinner />
                                </div>
                            </TableCell>
                        </TableRow>
                    ) : conversations.length > 0 || (listings && listings.length > 0) ? (
                        <>
                        {conversations.map((s) => (
                            <TableRow key={s.publicId}>
                                <TableCell className='w-[500px]'>
                                    <div className='flex'>
                                        <span className='w-6 mr-2 flex-shrink-0' />
                                        <Link
                                            to={`/listings/add?mode=ai&session=${s.publicId}`}
                                            className='flex h-[100px] w-[100px] flex-shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600'>
                                            <Sparkles className='h-7 w-7' />
                                        </Link>
                                        <div className='min-w-0 px-3'>
                                            <div>{s.title}</div>
                                            <div className='mt-1 text-xs'>{progressLabel(s)}</div>
                                            {s.files.length > 0 && (
                                                <div className='mt-1 truncate text-xs text-muted-foreground'>{s.files.map((f) => f.name).join(', ')}</div>
                                            )}
                                            <div className='mt-1.5 flex items-center gap-2'>
                                                <Badge variant='outline' className='gap-1 border-violet-200 bg-violet-50 text-violet-700'>
                                                    <Sparkles className='h-3 w-3' /> AI conversation
                                                </Badge>
                                                <span className='text-[11px] text-muted-foreground'>{relativeTime(s.lastMessageAt)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <Badge className='border border-violet-300 bg-violet-50 text-violet-700'>In progress</Badge>
                                </TableCell>
                                <TableCell>
                                    <div className='flex flex-nowrap items-center gap-2'>
                                        <Button type='button' size='sm' variant='outline' asChild>
                                            <Link to={`/listings/add?mode=ai&session=${s.publicId}`}>
                                                <MessageSquare className='h-3.5 w-3.5' />
                                                Continue
                                            </Link>
                                        </Button>

                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                        {(listings ?? []).map((listing, index) => (
                            <TableRow key={listing.id}>
                                <TableCell className='w-[500px]'>
                                    <div className='flex'>
                                        <span className='w-6 mr-2 flex-shrink-0 self-center text-right text-xs font-medium text-gray-400 tabular-nums'>
                                            {startIndex + index + 1}
                                        </span>
                                        <div
                                            className='rounded-lg h-[100px] w-[100px] flex-shrink-0'
                                            style={{
                                                background: `url(${import.meta.env.VITE_AWS_S3}/${
                                                    listing.medias.find(x => x.mediaType === 1 && x.isCover === 1)?.path ||
                                                    listing.medias.find(x => x.mediaType === 1)?.path
                                                })`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center center',
                                            }}
                                        />
                                        <div className='px-3'>
                                            <div>{listing.headline}</div>
                                            <div className='text-xs mt-1 whitespace-break-spaces'>{listing.rawAddress}</div>
                                            <div className='font-bold mt-2'>{Helper.formatCurrency(Number(listing.price))}</div>
                                            <div className='flex items-center gap-2 mt-1.5'>
                                                <Badge variant={listing.forSaleOrRentId === 1 ? 'outline' : 'secondary'}>
                                                    {listing.forSaleOrRentId === 1 ? 'Sale' : 'Rent'}
                                                </Badge>
                                                {listing.createChannel === 'ai' && (
                                                    <Badge variant='outline' className='gap-1 border-violet-200 bg-violet-50 text-violet-700' title='Created with AI'>
                                                        <Sparkles className='h-3 w-3' /> AI
                                                    </Badge>
                                                )}
                                                <span className='text-[11px] text-gray-700 font-mono bg-gray-100 px-1.5 py-0.5 rounded'>
                                                    {listing.listingReferenceNumber}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <Badge className="border border-blue-500 bg-blue-100 text-blue-800">
                                        Created
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    <div className='flex flex-nowrap items-center gap-2'>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    type='button'
                                                    size='sm'
                                                    variant='outline'
                                                    asChild
                                                >
                                                    <Link to={`/listings/edit/${listing.id}`}>
                                                        <Edit className='h-3.5 w-3.5' />
                                                        Edit
                                                    </Link>
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>Edit Listing</TooltipContent>
                                        </Tooltip>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                type='button'
                                                size='sm'
                                                variant='destructive'
                                                onClick={() => triggerAction(listing.id, 'delete')}
                                                >
                                                    <Trash className='h-3.5 w-3.5' />
                                                    Delete
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>Delete Listing</TooltipContent>
                                        </Tooltip>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                        </>
                    ) : (
                        <TableRow>
                            <TableCell colSpan={5} className="text-center py-4">
                                No listings found.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TooltipProvider>
    );
};

export default DraftListingTable;
